import { FC } from 'react';
import {
  format,
  parseISO,
  eachDayOfInterval,
  startOfMonth,
  endOfMonth,
  isSameMonth,
} from 'date-fns';
import { BarChart } from '@mui/x-charts/BarChart';

interface BarChartProps {
  chartData: any[];
}

const fillMissingDays = (data: any[]) => {
  if (data.length === 0) return [];

  // Get a date from the dataset to determine which month we're working with
  const referenceDate = parseISO(data[0].date);

  // Create start and end dates for the full month
  const monthStart = startOfMonth(referenceDate);
  const monthEnd = endOfMonth(referenceDate);

  // Create array of all days in the month
  const allDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Filter data to only include entries from this month and create a lookup map
  const dataMap = new Map(
    data
      .filter((item) => isSameMonth(parseISO(item.date), referenceDate))
      .map((item) => [format(parseISO(item.date), 'yyyy-MM-dd'), item])
  );

  // Create the full month dataset, using 0 for missing days
  return allDays.map((date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return (
      dataMap.get(dateStr) || {
        date: dateStr,
        hours_on_day: 0,
        hours_on_night: 0,
      }
    );
  });
};

export const CustomBarChart: FC<BarChartProps> = ({ chartData }) => {
  const filledChartData = fillMissingDays(chartData);

  const hasData = filledChartData.some(
    (item) => item.hours_on_day > 0 || item.hours_on_night > 0
  );

  return (
    <BarChart
      dataset={filledChartData}
      colors={['#6437E4', '#B49AF3']} // Different colors for day and night
      borderRadius={10}
      margin={{ bottom: 36, left: 28, right: 18 }}
      series={[
        {
          dataKey: 'hours_on_day',
          label: 'Hours on (Day):',
          valueFormatter: (value: number | null) => (value ? `${value}` : '0'),
          stack: 'total',
        },
        {
          dataKey: 'hours_on_night',
          label: 'Hours on (Night):',
          valueFormatter: (value: number | null) => (value ? `${value}` : '0'),
          stack: 'total',
        },
      ]}
      xAxis={[
        {
          dataKey: 'date',
          scaleType: 'band',
          valueFormatter: (value: string) => format(parseISO(value), 'MMM d'),
        },
      ]}
      yAxis={[
        {
          disableTicks: true,
        },
      ]}
      slotProps={{
        legend: {
          hidden: !hasData, // Show legend to distinguish between day and night
          labelStyle: {
            fontSize: 12,
            fontWeight: 400,
          },
        },
      }}
    />
  );
};
